import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router';
import { App } from './App';
import { bootDeadline, prefetchBoot } from './boot';
import { LocaleProvider, preloadLocale, resolveInitialLocale } from './i18n';
import { startOffline } from './offline/client';
import { preloadRoute } from './pages/lazy';
import { startTheme } from './theme';
import './styles/brain-ds.css';
import './styles/shell-nav.css';

const queryClient = new QueryClient({
  defaultOptions: {
    // `offlineFirst`: with the browser offline the query still runs once, so the service worker can
    // answer reads from its cache and the comment/consent writes can fall back to the outbox
    // (planning-app.md §4.4); the default `online` mode would pause them until the network is back.
    queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false, networkMode: 'offlineFirst' },
    mutations: { networkMode: 'offlineFirst' },
  },
});

// The first room's chunk downloads alongside /api/me rather than after it (planning-app.md §4.5).
const initialLocale = resolveInitialLocale();
preloadRoute(window.location.pathname, initialLocale);
// French and the pseudo-locale are separate chunks; wait for the one we start in so nothing flashes English.
// The session and the first room's data are awaited too (briefly), so the first render is the page itself.
Promise.race([Promise.allSettled([preloadLocale(initialLocale), prefetchBoot(queryClient, window.location.pathname)]), bootDeadline()]).finally(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <LocaleProvider initialLocale={initialLocale}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </LocaleProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
});

// The service worker (src/sw.ts, production builds only), the online/offline state and the outbox.
startOffline();
// Colour scheme: device setting unless You → Appearance pinned one (index.html paints it before React).
startTheme();
